import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  Pressable,
  Text,
  Alert,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { resetOnboardingState } from '../utils/onboardingStorage';
import { Theme } from '../components/ui';

// Environment detection for debug visibility
let isDev = false;

// Check if running on localhost (web only)
const isLocalhost = typeof window !== 'undefined' && 
  typeof window.location !== 'undefined' &&
  (window.location.hostname === 'localhost' || 
   window.location.hostname === '127.0.0.1' ||
   window.location.hostname === '0.0.0.0');

// Check for console command to enable debug (use localStorage for persistence)
const isDebugEnabled = typeof window !== 'undefined' && 
  typeof localStorage !== 'undefined' &&
  localStorage.getItem('MENTORIST_DEBUG_ENABLED') === 'true';

// Check React Native dev mode
const isReactNativeDev = typeof __DEV__ !== 'undefined' && __DEV__ === true;

// Only show on localhost, when debug is enabled via console, or in React Native dev mode
isDev = isLocalhost || isDebugEnabled || isReactNativeDev;

// Add global function to enable debug via console
if (typeof window !== 'undefined') {
  (window as any).enableMentorDebug = () => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('MENTORIST_DEBUG_ENABLED', 'true');
      console.log('🔧 Mentorist debug mode enabled. Refresh the page to see debug controls.');
    }
  };
  
  (window as any).disableMentorDebug = () => {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('MENTORIST_DEBUG_ENABLED');
      console.log('🔧 Mentorist debug mode disabled. Refresh the page to hide debug controls.');
    }
  };
}

console.log('OnboardingDebugButton rendered, debug mode:', isDev, {
  isLocalhost,
  isDebugEnabled,
  isReactNativeDev,
  hostname: typeof window !== 'undefined' && typeof window.location !== 'undefined' ? window.location.hostname : 'N/A (React Native)'
});

interface OnboardingData {
  currentStep: number;
  totalSteps: number;
  answers: Array<{
    question: string;
    answer: any;
    type: string;
  }>;
  completed: boolean;
}

const OnboardingDebugButton: React.FC = () => {
  const [showMenu, setShowMenu] = useState(false);
  const navigation = useNavigation();

  // Only show in development mode
  if (!isDev) {
    return null;
  }

  const handleResetOnboarding = () => {
    console.log('🚨 handleResetOnboarding function called');
    try {
      // Use window.confirm for web, Alert for native
      if (Platform.OS === 'web') {
        console.log('🌐 Using window.confirm for web');
        const confirmed = window.confirm('Reset Onboarding\n\nThis will reset your onboarding state and log you out. You can then register a new account or sign back in to see the onboarding flow. Continue?');
        if (confirmed) {
          console.log('✅ User confirmed reset');
          handleResetLogic();
        } else {
          console.log('❌ User cancelled reset');
        }
      } else {
        Alert.alert(
          'Reset Onboarding',
          'This will reset your onboarding state and log you out. You can then register a new account or sign back in to see the onboarding flow. Continue?',
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Reset & Logout',
              style: 'destructive',
              onPress: async () => {
                handleResetLogic();
              },
            },
          ]
        );
      }
    } catch (error) {
      console.error('🚨 Error in handleResetOnboarding:', error);
      if (Platform.OS === 'web') {
        window.alert('Error: Failed to show reset dialog');
      } else {
        Alert.alert('Error', 'Failed to show reset dialog');
      }
    }
  };

    const handleResetLogic = async () => {
    console.log('🔥 RESET & LOGOUT FUNCTION STARTED');
    try {
      console.log('🔄 Starting onboarding reset...');
      
      // Check current state before reset
      const { isOnboardingComplete } = require('../utils/onboardingStorage');
      const beforeReset = await isOnboardingComplete();
      console.log('📊 Onboarding complete BEFORE reset:', beforeReset);
      
      await resetOnboardingState();
      console.log('✅ Onboarding state reset successfully');
      
      // Check state after reset
      const afterReset = await isOnboardingComplete();
      console.log('📊 Onboarding complete AFTER reset:', afterReset);
      
      setShowMenu(false);
      
      // Sign out the user (this is the key!)
      console.log('🚪 Signing out user...');
      if (Platform.OS === 'web') {
        // Web
        const { auth } = require('../config/firebase');
        const { signOut } = require('firebase/auth');
        await signOut(auth);
      } else {
        // Native
        const auth = require('@react-native-firebase/auth').default;
        await auth().signOut();
      }
      console.log('✅ User signed out');
      
      // Force page reload to ensure state is cleared
      console.log('🌐 Forcing page reload...');
      console.log('📱 Platform check:', Platform.OS);
      
      if (Platform.OS === 'web') {
        console.log('🌐 About to call window.location.reload()...');
        setTimeout(() => {
          console.log('🌐 Executing reload now...');
          window.location.reload();
        }, 100);
      } else {
        console.log('📱 Native platform - showing alert instead');
        Alert.alert(
          'Success', 
          'Onboarding reset. Please register a new account to see the full onboarding flow.'
        );
      }
      
      console.log('✅ Reset complete');
    } catch (error) {
      console.error('❌ Error resetting onboarding:', error);
      if (Platform.OS === 'web') {
        window.alert(`Error: Failed to reset onboarding: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } else {
        Alert.alert('Error', `Failed to reset onboarding: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  const handleRestartOnboarding = () => {
    // Use window.confirm for web, Alert for native
    if (Platform.OS === 'web') {
      const confirmed = window.confirm('Restart Onboarding\n\nThis will restart the onboarding flow without logging you out. Your previous answers will be cleared. Continue?');
      if (confirmed) {
        handleRestartLogic();
      }
    } else {
      Alert.alert(
        'Restart Onboarding',
        'This will restart the onboarding flow without logging you out. Your previous answers will be cleared. Continue?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Restart',
            style: 'default',
            onPress: async () => {
              handleRestartLogic();
            },
          },
        ]
      );
    }
  };

    const handleRestartLogic = async () => {
    try {
      console.log('🔄 Restarting onboarding (staying logged in)...');
      
      // Check current state before reset
      const { isOnboardingComplete } = require('../utils/onboardingStorage');
      const beforeReset = await isOnboardingComplete();
      console.log('📊 Onboarding complete BEFORE restart:', beforeReset);
      
      await resetOnboardingState();
      console.log('✅ Onboarding state reset, restarting flow');
      
      // Check state after reset
      const afterReset = await isOnboardingComplete();
      console.log('📊 Onboarding complete AFTER restart:', afterReset);
      
      setShowMenu(false);
      
      // Force page reload to ensure state is cleared
      console.log('🌐 Forcing page reload for restart...');
      console.log('📱 Platform check:', Platform.OS);
      
      if (Platform.OS === 'web') {
        console.log('🌐 About to call window.location.reload() for restart...');
        setTimeout(() => {
          console.log('🌐 Executing reload for restart now...');
          window.location.reload();
        }, 100);
      } else {
        console.log('📱 Native platform - showing alert instead');
        Alert.alert(
          'Success', 
          'Onboarding restarted. Complete the flow to access the dashboard.'
        );
      }
    } catch (error) {
      console.error('❌ Error restarting onboarding:', error);
      if (Platform.OS === 'web') {
        window.alert('Error: Failed to restart onboarding');
      } else {
        Alert.alert('Error', 'Failed to restart onboarding');
      }
    }
  };

  const handleSkipToEnd = () => {
    // Use window.confirm for web, Alert for native
    if (Platform.OS === 'web') {
      const confirmed = window.confirm('Skip to Dashboard\n\nThis will mark onboarding as complete and take you to the dashboard.');
      if (confirmed) {
        // TODO: Implement skip functionality
        console.log('Skip to end not implemented yet');
        setShowMenu(false);
      }
    } else {
      Alert.alert(
        'Skip to Dashboard',
        'This will mark onboarding as complete and take you to the dashboard.',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Skip',
            onPress: () => {
              // TODO: Implement skip functionality
              console.log('Skip to end not implemented yet');
              setShowMenu(false);
            },
          },
        ]
      );
    }
  };

  const handleLogout = () => {
    // Use window.confirm for web, Alert for native
    if (Platform.OS === 'web') {
      const confirmed = window.confirm('Logout\n\nThis will log you out of the app. Are you sure?');
      if (confirmed) {
        handleLogoutLogic();
      }
    } else {
      Alert.alert(
        'Logout',
        'This will log you out of the app. Are you sure?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Logout',
            style: 'destructive',
            onPress: async () => {
              handleLogoutLogic();
            },
          },
        ]
      );
    }
  };

    const handleLogoutLogic = async () => {
    try {
      console.log('🚪 Logging out user...');
      
      // Check if user is already signed out
      let currentUser = null;
      if (Platform.OS === 'web') {
        const { auth } = require('../config/firebase');
        currentUser = auth.currentUser;
      } else {
        const auth = require('@react-native-firebase/auth').default;
        currentUser = auth().currentUser;
      }
      
      if (!currentUser) {
        console.log('ℹ️ User already signed out, navigating to intro screen...');
        setShowMenu(false);
        (navigation as any).reset({
          index: 0,
          routes: [{ name: 'IntroSlides' }],
        });
        return;
      }
      
      // Import auth based on platform and sign out
      if (Platform.OS === 'web') {
        // Web: Sign out from BOTH Firebase instances
        const { auth } = require('../config/firebase');
        const { signOut } = require('firebase/auth');
        
        // 1. Sign out from v12 modular auth
        await signOut(auth);
        console.log('✅ Signed out from Firebase v12');
        
        // 2. Also sign out from v5 compat auth (if it was loaded for OAuth)
        if ((window as any).firebase?.auth) {
          await (window as any).firebase.auth().signOut();
          console.log('✅ Signed out from Firebase v5 compat');
        }
      } else {
        // Native
        const auth = require('@react-native-firebase/auth').default;
        await auth().signOut();
        
        // Navigate to intro screen on native
        (navigation as any).reset({
          index: 0,
          routes: [{ name: 'IntroSlides' }],
        });
      }
      
      setShowMenu(false);
      console.log('✅ User logged out successfully');
    } catch (error) {
      console.error('❌ Error logging out:', error);
      if (Platform.OS === 'web') {
        window.alert(`Error: Failed to logout: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } else {
        Alert.alert('Error', `Failed to logout: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  return (
    <>
      {/* Backdrop to close menu when tapping outside */}
      {showMenu && (
        <Pressable 
          style={styles.backdrop}
          onPress={() => setShowMenu(false)}
        />
      )}
      
      <View style={styles.container}>
        <Pressable
          style={styles.debugButton}
          onPress={() => {
            console.log('Debug button pressed, showMenu:', showMenu);
            setShowMenu(!showMenu);
          }}
        >
          <Text style={styles.debugButtonText}>⚙️</Text>
        </Pressable>

        {showMenu && (
          <View style={styles.dropdown}>
            {/* Close button at top right */}
            <Pressable
              style={styles.closeButton}
              onPress={() => setShowMenu(false)}
            >
              <Text style={styles.closeButtonText}>✕</Text>
            </Pressable>
            
            <Pressable
              style={styles.menuItem}
              onPress={() => {
                console.log('Reset & Logout pressed');
                handleResetOnboarding();
              }}
            >
              <Text style={styles.menuItemText}>🔄 Reset & Logout</Text>
            </Pressable>
            
            <Pressable
              style={styles.menuItem}
              onPress={() => {
                console.log('Restart onboarding pressed');
                handleRestartOnboarding();
              }}
            >
              <Text style={styles.menuItemText}>🔁 Restart Onboarding</Text>
            </Pressable>
            
            <Pressable
              style={styles.menuItem}
              onPress={() => {
                console.log('Logout pressed');
                handleLogout();
              }}
            >
              <Text style={styles.menuItemText}>🚪 Logout</Text>
            </Pressable>
            
            <Pressable
              style={styles.menuItem}
              onPress={() => {
                console.log('Skip to dashboard pressed');
                handleSkipToEnd();
              }}
            >
              <Text style={styles.menuItemText}>⏭️ Skip to Dashboard</Text>
            </Pressable>
            
            <Pressable
              style={styles.menuItem}
              onPress={() => {
                console.log('Debug Console pressed');
                setShowMenu(false);
                (navigation as any).navigate('Debug');
              }}
            >
              <Text style={styles.menuItemText}>🔧 Debug Console</Text>
            </Pressable>

            
            <Pressable
              style={[styles.menuItem, styles.lastMenuItem]}
              onPress={() => {
                console.log('Close pressed');
                setShowMenu(false);
              }}
            >
              <Text style={styles.menuItemText}>❌ Close</Text>
            </Pressable>
          </View>
        )}
      </View>
    </>
  );
};

console.log('Debug mode check:', { 
  isDev, 
  isLocalhost,
  isDebugEnabled,
  isReactNativeDev,
  hostname: typeof window !== 'undefined' && typeof window.location !== 'undefined' ? window.location.hostname : 'N/A (React Native)'
});

const styles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9998,
  },
  container: {
    position: 'absolute',
    bottom: 20,
    right: 15,
    zIndex: 9999,
  },
  debugButton: {
    backgroundColor: 'rgba(102, 102, 102, 0.8)',
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
  },
  debugButtonText: {
    fontSize: 14,
  },
  closeButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  closeButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#666',
  },
  dropdown: {
    position: 'absolute',
    bottom: 45,
    right: 0,
    paddingTop: 36,
    backgroundColor: Theme.colors.surface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Theme.colors.border,
    minWidth: 180,
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    zIndex: 10000,
  },
  menuItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: Theme.colors.border,
  },
  lastMenuItem: {
    borderBottomWidth: 0,
  },
  menuItemText: {
    fontSize: 14,
    color: Theme.colors.textPrimary,
    fontFamily: Theme.typography.fontFamily.regular,
  },
});

export default OnboardingDebugButton; 